import React, { useEffect, useState } from "react";
import EditorReadonly from "../../general/EditorReadonly";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import Utils from "../../../util/Utils";
import { Button } from "primereact/button";
import { ConfirmPopup, confirmPopup } from "primereact/confirmpopup";
import NotificationPopUp from "../../../util/NotificationPopUp";
import { apisType } from "../../../serviceapi/ApiImports";
import { apisStore } from "../../../context/GlobalStates";
import { Tooltip } from "primereact/tooltip";
import "./PipelineHistory.scss";

type WriterHistoryProps = {
  pipelineId?: string;
};

enum TabType {
  GENERAL = "General",
  INPUT = "Input",
  OUTPUT = "Output",
  STRING = "String",
  CODE = "Code",
}

const PipelineHistory = ({ pipelineId }: WriterHistoryProps) => {
  const [pipelineHistories, setPipelineHistories] = useState<Array<any>>([]);
  const [selectedPipeline, setSelectedPipeline] = useState<any>(undefined);
  const [pipelineText, setPipelineText] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(true);
  const [activeTab, setActiveTab] = useState<TabType>(TabType.GENERAL);
  const [activeSubTab, setActiveSubTab] = useState<number>(0);

  const apis: apisType = apisStore.getState().dataPusher;

  useEffect(() => {
    if (pipelineId) {
      updateWriters();
    }
  }, [pipelineId]);

  const updateWriters = () => {
    if (!pipelineId) {
      return;
    }
    apis.writerBackupApi
      ?.getBackups([pipelineId])
      .then((res: any) => {
        setPipelineHistories(res.data);
        setLoading(false);
      })
      .catch((err) => NotificationPopUp.handleGenericError(err));
  };

  const handlePipleineSelect = (selectedPipeline: any) => {
    setSelectedPipeline(selectedPipeline);
    if (selectedPipeline) {
      apis.writerBackupApi
        ?.getConfigurationBackup(selectedPipeline.id)
        .then((res: any) => {
          setPipelineText(JSON.stringify(res.data, null, 4));
          setActiveSubTab(0);
        })
        .catch((err) => NotificationPopUp.handleGenericError(err));
    }
  };
  const timestampToDate = (rowData: any) =>
    Utils.timestampToReadableDate(rowData.timestamp);

  const restore = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    id: number
  ) => {
    confirmPopup({
      target: event.currentTarget,
      message: "Are you sure you want to restore pipeline with this backup?",
      icon: "pi pi-exclamation-triangle",
      accept: () => {
        apis.writerBackupApi
          ?.restoreBackup(pipelineId!, id)
          .then(() => {
            NotificationPopUp.show(
              "Pipeline was restored successfully",
              "success"
            );
          })
          .catch((err) => NotificationPopUp.handleGenericError(err));
      },
    });
  };

  const remove = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    id: number
  ) => {
    confirmPopup({
      target: event.currentTarget,
      message: "Are you sure you want to remove pipeline back up?",
      icon: "pi pi-exclamation-triangle",
      accept: () => {
        apis.writerBackupApi
          ?.deleteBackup([id])
          .then(() => {
            NotificationPopUp.show(
              "Pipeline backup removed successfully",
              "success"
            );
            updateWriters();
          })
          .catch((err) => console.log(err));
      },
    });
  };

  const getTabContent = () => {
    try {
      const parsedText = JSON.parse(JSON.parse(pipelineText));

      if (activeTab === TabType.INPUT) {
        const inputs = parsedText.inputConfig ?? [];
        if (Array.isArray(inputs) && inputs.length > 0) {
          const selectedInput = inputs[activeSubTab] ?? inputs[0];
          return JSON.stringify(selectedInput, null, 4);
        }
        return "Input configuration is empty or undefined.";
      }

      if (activeTab === TabType.OUTPUT) {
        const outputs = parsedText.outputConfig ?? [];
        if (Array.isArray(outputs) && outputs.length > 0) {
          const selectedOutput = outputs[activeSubTab] ?? outputs[0];
          return JSON.stringify(selectedOutput, null, 4);
        }
        return "Output configuration is empty or undefined.";
      }

      if (activeTab === TabType.STRING) {
        return pipelineText;
      }

      if (activeTab === TabType.CODE) {
        return (
          Utils.base64Decode(parsedText.processorConfig.script) ||
          "// Code is empty or undefined."
        );
      }

      delete parsedText.inputConfig;
      delete parsedText.processorConfig;
      delete parsedText.outputConfig;

      return JSON.stringify(parsedText, null, 4);
    } catch {
      if (pipelineText === "") {
        return "Please select a pipeline to view the configuration.";
      }
    }
  };

  const functionality = (rowData: any) => (
    <div className="button-container">
      <i
        id={`user-icon-${rowData.id}`}
        className={"pi pi-user my-auto mx-2"}
        data-pr-tooltip={rowData.editor?.username ?? "Unknown"}
        data-pr-position="bottom"
      />
      <Tooltip target={`#user-icon-${rowData.id}`} position="bottom" />
      <Button
        icon="pi pi-history"
        className="button p-button-info p-0"
        onClick={(event) => restore(event, rowData.id)}
        tooltip="Restore writer config"
      />
      <Button
        icon="pi pi-trash"
        className="button p-button-danger p-0"
        onClick={(event) => remove(event, rowData.id)}
        tooltip="Remove writer config backup"
      />
    </div>
  );

  const getSubtabs = () => {
    try {
      const parsedText = JSON.parse(JSON.parse(pipelineText));
      const configs =
        activeTab === TabType.INPUT
          ? parsedText.inputConfig ?? []
          : parsedText.outputConfig ?? [];

      if (!Array.isArray(configs) || configs.length === 0) return null;

      return (
        <div className="subtab-container">
          {configs.map((_: any, index: number) => (
            <div
              key={index}
              className={`subtab-button ${
                activeSubTab === index ? "active" : ""
              }`}
              onClick={() => setActiveSubTab(index)}
            >
              {`${configs[index].id}`}
            </div>
          ))}
        </div>
      );
    } catch {
      return null;
    }
  };

  return (
    <div className="writer-history-full-window">
      <div className="left-side">
        <DataTable
          value={pipelineHistories}
          style={{ maxHeight: "50vw" }}
          sortField="timestamp"
          sortOrder={-1}
          selectionMode="single"
          selection={selectedPipeline}
          onSelectionChange={(e) => handlePipleineSelect(e.value)}
          loading={loading}
          size="small"
        >
          <Column field="timestamp" header="Date" body={timestampToDate} />
          <Column className="column" body={functionality} />
        </DataTable>
      </div>
      <div className="right-side">
        <div className="button-container-scheme-picker">
          {[
            TabType.GENERAL,
            TabType.INPUT,
            TabType.OUTPUT,
            TabType.STRING,
            TabType.CODE,
          ].map((tab) => (
            <div
              key={tab}
              className={`tab-view-button ${activeTab === tab ? "active" : ""}`}
              onClick={() => setActiveTab(tab as TabType)}
            >
              {tab}
            </div>
          ))}
        </div>
        {(activeTab === TabType.INPUT || activeTab === TabType.OUTPUT) &&
          getSubtabs()}
        <EditorReadonly text={getTabContent()} />
      </div>
      <ConfirmPopup />
    </div>
  );
};

export default PipelineHistory;
